import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { BrandLockup } from "@/shell/CandidateFlow";

type Stage = "email" | "otp" | "sending" | "verifying";

const T = {
  ink: "#17131F",
  paper: "#FBFAFE",
  accent: "var(--accent)",
  slate: "#7C7590",
  border: "#E4E0F4",
  error: "#B91C1C",
  white: "#FFFFFF",
};

export function CandidatePortalLogin() {
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resent, setResent] = useState(false);

  async function handleSend(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setStage("sending");
    try {
      const res = await api.post<{ ok: boolean; already_authed?: boolean; error?: string; cooldown?: number }>(
        "/candidate/portal/otp/send",
        { email }
      );
      if (res.already_authed) {
        navigate("/candidate/home", { replace: true });
        return;
      }
      if (!res.ok) {
        setError(res.error ?? "Something went wrong.");
        setStage("email");
        return;
      }
      setStage("otp");
    } catch {
      setError("Could not connect. Please try again.");
      setStage("email");
    }
  }

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setStage("verifying");
    try {
      const res = await api.post<{ ok: boolean; error?: string; next?: string }>(
        "/candidate/portal/otp/verify",
        { code }
      );
      if (res.ok) {
        navigate(res.next ?? "/candidate/home", { replace: true });
        return;
      }
      setError(res.error ?? "Incorrect code.");
      setStage("otp");
    } catch {
      setError("Could not connect. Please try again.");
      setStage("otp");
    }
  }

  async function handleResend() {
    setResent(false);
    setError("");
    setResendDisabled(true);
    try {
      await api.post("/candidate/portal/otp/resend", {});
      setResent(true);
    } catch {
      /* silent */
    }
    setTimeout(() => setResendDisabled(false), 30_000);
  }

  const loading = stage === "sending" || stage === "verifying";

  return (
    <div style={{ minHeight: "100dvh", background: T.paper, display: "flex", flexDirection: "column" }}>
      {/* Header — no distinct band colour: candidate SPA pages use --paper
          throughout (design-system/system.md), same as the page body below. */}
      <div style={{ padding: "40px 24px 32px", textAlign: "center" }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 2 }}>
          <BrandLockup />
        </div>
        <h1 style={{ margin: "10px 0 0", fontSize: 26, fontWeight: 700, color: T.ink, lineHeight: 1.2 }}>
          {stage === "otp" || stage === "verifying" ? "Check your email" : "Sign in"}
        </h1>
        <p style={{ margin: "8px 0 0", fontSize: 14, color: T.slate }}>
          {stage === "otp" || stage === "verifying"
            ? `We sent a 6-digit code to ${email}`
            : "Access your interview sessions"}
        </p>
      </div>

      {/* Card */}
      <div style={{ flex: 1, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "32px 16px" }}>
        <div
          style={{
            width: "100%",
            maxWidth: 400,
            background: T.white,
            borderRadius: 16,
            boxShadow: "0 24px 64px rgba(27,26,36,0.10), 0 4px 12px rgba(27,26,36,0.06)",
            padding: "32px 28px",
          }}
        >
          {error && (
            <div style={{ marginBottom: 20, padding: "12px 14px", background: "#FEF2F2", borderRadius: 8, border: "1px solid #FECACA" }}>
              <p style={{ margin: 0, fontSize: 13.5, color: T.error }}>{error}</p>
            </div>
          )}

          {(stage === "email" || stage === "sending") && (
            <form onSubmit={handleSend}>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: T.ink, marginBottom: 6 }}>
                Email address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                placeholder="you@example.com"
                style={{
                  display: "block",
                  width: "100%",
                  padding: "11px 14px",
                  fontSize: 15,
                  border: `1px solid ${T.border}`,
                  borderRadius: 8,
                  outline: "none",
                  color: T.ink,
                  background: T.paper,
                  boxSizing: "border-box",
                }}
              />
              <button
                type="submit"
                disabled={loading || !email.includes("@")}
                style={{
                  display: "block",
                  width: "100%",
                  marginTop: 16,
                  padding: "13px 0",
                  fontSize: 15,
                  fontWeight: 600,
                  color: T.white,
                  background: loading ? T.slate : T.accent,
                  border: "none",
                  borderRadius: 8,
                  cursor: loading ? "default" : "pointer",
                  transition: "background 0.15s",
                }}
              >
                {loading ? "Sending code…" : "Send login code"}
              </button>
            </form>
          )}

          {(stage === "otp" || stage === "verifying") && (
            <form onSubmit={handleVerify}>
              <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: T.ink, marginBottom: 6 }}>
                6-digit code
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                required
                autoFocus
                placeholder="000000"
                style={{
                  display: "block",
                  width: "100%",
                  padding: "11px 14px",
                  fontSize: 22,
                  letterSpacing: "0.2em",
                  border: `1px solid ${T.border}`,
                  borderRadius: 8,
                  outline: "none",
                  color: T.ink,
                  background: T.paper,
                  boxSizing: "border-box",
                  textAlign: "center",
                }}
              />
              <button
                type="submit"
                disabled={loading || code.length !== 6}
                style={{
                  display: "block",
                  width: "100%",
                  marginTop: 16,
                  padding: "13px 0",
                  fontSize: 15,
                  fontWeight: 600,
                  color: T.white,
                  background: loading ? T.slate : T.accent,
                  border: "none",
                  borderRadius: 8,
                  cursor: loading ? "default" : "pointer",
                  transition: "background 0.15s",
                }}
              >
                {loading ? "Verifying…" : "Verify code"}
              </button>

              <div style={{ marginTop: 16, textAlign: "center" }}>
                {resent && (
                  <p style={{ margin: "0 0 8px", fontSize: 13, color: T.accent }}>New code sent.</p>
                )}
                <button
                  type="button"
                  disabled={resendDisabled}
                  onClick={handleResend}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: 13.5,
                    color: resendDisabled ? T.slate : T.accent,
                    cursor: resendDisabled ? "default" : "pointer",
                    textDecoration: "underline",
                    padding: 0,
                  }}
                >
                  {resendDisabled ? "Code sent — check your inbox" : "Resend code"}
                </button>
              </div>

              <div style={{ marginTop: 12, textAlign: "center" }}>
                <button
                  type="button"
                  onClick={() => { setStage("email"); setCode(""); setError(""); }}
                  style={{
                    background: "none", border: "none",
                    fontSize: 13, color: T.slate,
                    cursor: "pointer", textDecoration: "underline", padding: 0,
                  }}
                >
                  Use a different email
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
